import React from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageInfo, copyLanguageInfo } from '@/features/languages/languagesSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';
import { NumberText } from '@/components/Common/Field';

import { CatHeader } from '@/pages/Projects/Project';

export interface LanguageProps {
	index:				number;
	language:			LanguageInfo | null;
	setLanguage:		(language: LanguageInfo) => any;
	onClose:			() => any;
}

const Language = (props: LanguageProps) => {
  const { t } = useTranslation();

  const isNew = props.index == -2;

  const [language, setLanguage] = React.useState(null as LanguageInfo | null);
  const [changes, setChanges] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);

  React.useEffect(() => {
    setLanguage(props.language);
  }, [props.language]);

  const nameInvalid = React.useMemo(() => {
    return !language || language.name.trim() == '';
  }, [language]);

  const codeInvalid = React.useMemo(() => {
    return !language || language.code.trim() == '';
  }, [language]);

  const updateName = React.useCallback((val: string) => {
    if (!language)
      return;

    let nlang = copyLanguageInfo(language);
    nlang.name = val;

    setLanguage(nlang);
    setChanges(true);
  }, [language, setLanguage]);

  const updateCode = React.useCallback((val: string) => {
    if (!language)
      return;

    let nlang = copyLanguageInfo(language);
    nlang.code = val;

    setLanguage(nlang);
    setChanges(true);
  }, [language, setLanguage]);

  const updatePriority = React.useCallback((val: string) => {
    if (!language)
      return;

    let nlang = copyLanguageInfo(language);
    nlang.priority = parseInt(val) || 0;

    setLanguage(nlang);
    setChanges(true);
  }, [language, setLanguage]);

  const handleSave = React.useCallback(() => {
    if (!language || nameInvalid || codeInvalid) {
      setTriedSave(true);
      return;
    }

    setTriedSave(false);
    props.setLanguage(language);
    setChanges(false);
    props.onClose();
  }, [language, props.setLanguage, nameInvalid, codeInvalid]);

  if (!language)
    return;

  return (
  <React.Fragment>
    <FlexRow direction="column"
             align="start"
             className="px-[32px]">
      <FlexRow direction="column"
		       align="start"
		       className="py-0 gap-[16px] max-w-[620px]">
	    <CatHeader caption={isNew ? 'languages:new_language:caption' : 'languages:language:edit_language'} />

		<Input label={t('languages:language_name') + '*'}
		       isInvalid={triedSave && nameInvalid}
		       errorMessage={t('languages:language:name:invalid')}
		       value={language ? language.name : ''}
		       onValueChange={updateName} />
		<Input label={t('languages:language_code') + '*'}
		       isInvalid={triedSave && codeInvalid}
		       errorMessage={t('languages:language:code:invalid')}
		       value={language ? language.code : ''}
		       onValueChange={updateCode} />
		<Input label={t('languages:priority')}
		       type="number"
		       value={language ? language.priority.toString() : '0'}
		       onValueChange={updatePriority} />
	  </FlexRow>
    </FlexRow>
    <FlexRow className="absolute bottom-0 p-[32px] bg-white z-10 justify-end gap-[16px]">
      <Button className="uppercase"
              caption={t('languages:cancel')}
              onClick={() => props.onClose()}
              layout="white" />
      <Button caption={t('languages:save')}
              icon="save"
              isDisabled={!changes || (triedSave && (nameInvalid || codeInvalid))}
              onClick={() => handleSave()} />
    </FlexRow>
  </React.Fragment>
  );
};

export default Language;
